import React from 'react'

import { useGoogleLogin } from '@react-oauth/google'
import { observer } from 'mobx-react'
// @ts-ignore
import { appleAuthHelpers } from 'react-apple-signin-auth'

import cn from 'classnames'

import { SocialConnect, SocialConnectValues, TwitterEventType } from 'utils'

import { toast } from 'App'

import { useStore } from 'store'

import ActivatedIcon from 'sources/images/activatedIcon.svg'
import { ReactComponent as Apple } from 'sources/images/apple.svg'
import Google from 'sources/images/googleColor.svg'
import { ReactComponent as PlusIcon } from 'sources/images/plus.svg'
import { ReactComponent as Twitter } from 'sources/images/twitter-x.svg'

import styles from './styles.module.scss'

interface IProps {
  currentUser?: boolean
}

const getSocialField = (type: SocialConnect, value: boolean) => {
  switch (type) {
    case SocialConnect.Google:
      return { IsGoogleConnected: value }
    case SocialConnect.Apple:
      return { IsAppleConnected: value }
    case SocialConnect.Twitter:
      return { IsTwitterConnected: value }
  }
}

const UserInfoConnectSocial: React.FC<IProps> = observer(() => {
  const { profile, loading, login: loginStore } = useStore()
  const { selectedUser } = profile

  const socials = [
    {
      type: SocialConnect.Google,
      icon: <img src={Google} alt="google" className={styles.typeIcon} />,
      connected: selectedUser.IsGoogleConnected,
    },
    {
      type: SocialConnect.Apple,
      icon: (
        <Apple
          className={cn(styles.typeIcon, styles.apple)}
          style={{ padding: '0 1px' }}
        />
      ),
      connected: selectedUser.IsAppleConnected,
    },
    {
      type: SocialConnect.Twitter,
      icon: <Twitter className={cn(styles.typeIcon, styles.twitter)} />,
      connected: selectedUser.IsTwitterConnected,
    },
  ]

  const onClickConnect = (type: SocialConnect) => {
    if (type === SocialConnect.Google) {
      handleGoogleConnect()
    }

    if (type === SocialConnect.Apple) {
      handleAppleLogin()
    }

    if (type === SocialConnect.Twitter) {
      handleTwitterLogin()
    }
  }

  const onClickDisconnect = async (type: SocialConnect) => {
    if (profile.getLoginProvider() === type) {
      return toast({
        type: 'warning',
        message: `Please login in another way to remove ${type}`,
      })
    }

    try {
      loading.setLoading(true)
      await loginStore.disconnectSocial(type)

      if (type === SocialConnect.Twitter) {
        profile.setSelectedUser({
          ...profile.selectedUser,
          IsTwitterConnected: false,
          TwitterId: '',
          TwitterName: '',
        })
      } else {
        profile.setSelectedUser({
          ...profile.selectedUser,
          ...getSocialField(type, false),
        })
      }

      toast({
        type: 'success',
        message: `You have successfully disconnected ${type}`,
      })
      loading.setLoading(false)
    } catch (e) {
      loading.setLoading(false)
      toast({
        type: 'error',
        message: 'Disconnect Failed',
      })
      console.log('e', e)
    }
  }

  const onConnectSuccess = (type: SocialConnect) => {
    profile.setSelectedUser({
      ...profile.selectedUser,
      ...getSocialField(type, true),
    })
    toast({
      type: 'success',
      message: `You have successfully connected ${type}`,
    })
  }

  // GOOGLE ---------------------------------------⬇︎⬇︎⬇︎
  const handleGoogleConnect = useGoogleLogin({
    onSuccess: async codeResponse => {
      try {
        loading.setLoading(true)
        await loginStore.connectSocial(
          codeResponse.access_token,
          SocialConnect.Google
        )
        onConnectSuccess(SocialConnect.Google)
        loading.setLoading(false)
      } catch (e: any) {
        loading.setLoading(false)
        toast({
          type: 'error',
          message: e.message || 'Connect Failed',
        })
        console.log('e', e)
      }
    },
    onError: error => {
      toast({
        type: 'error',
        message: 'Connect Failed',
      })
      console.log('Connect Failed:', error)
    },
  })
  // GOOGLE ---------------------------------------⬆︎⬆︎⬆︎

  // APPLE ---------------------------------------⬇︎⬇︎⬇︎
  const handleAppleLogin = async () => {
    try {
      appleAuthHelpers.signIn({
        authOptions: {
          clientId: 'io.authencity.app.login.identifier',
          redirectURI: window.location.origin,
          scope: 'email name',
          state: 'state',
          nonce: 'nonce',
          usePopup: true,
        },
        onSuccess: async (appleResponse: any) => {
          try {
            loading.setLoading(true)
            await loginStore.connectSocial(
              appleResponse.authorization.id_token,
              SocialConnect.Apple
            )
            onConnectSuccess(SocialConnect.Apple)
            loading.setLoading(false)
          } catch (e: any) {
            loading.setLoading(false)
            toast({
              type: 'error',
              message: e.message || 'Connect Failed',
            })
            console.log('e', e)
          }
        },
        onError: (e: any) => {
          console.log(e)
          if (e.error === 'popup_closed_by_user') return
          toast({
            type: 'error',
            message: 'Connect Failed',
          })
          console.log('Connect Failed:', e)
        },
      })
    } catch (e) {}
  }
  // APPLE ---------------------------------------⬆︎⬆︎⬆︎

  // TWITTER ---------------------------------------⬇︎⬇︎⬇︎
  const handleTwitterLogin = async () => {
    try {
      const twitterUrl = await profile.requestToken(
        TwitterEventType.ConnectSocial
      )
      sessionStorage.setItem('connect_twitter', String(new Date()))
      // window.location.href = twitterUrl
      const win = window.open(twitterUrl, '_self')
      if (win) {
        win.focus()
      }
    } catch (e) {
      toast({
        type: 'error',
        message: 'Connect Failed',
      })
    }
  }
  // TWITTER ---------------------------------------⬆︎⬆︎⬆︎

  const profileLoginProvider =
    profile.loginProvider || profile.loginProviderSession

  return (
    <div className={cn(styles.container)}>
      <div className={styles.title}>Connect social media</div>
      <div className={styles.socialsBox}>
        {socials.map(item => (
          <button
            key={item.type}
            className={cn({
              [styles.socialBtn]: true,
              [styles.socialBtnDisabled]: item.connected,
            })}
            onClick={
              !item.connected ? () => onClickConnect(item.type) : undefined
            }
          >
            <div className={styles.socialType}>
              <div className={styles.typeIconBox}>{item.icon}</div>
              {SocialConnectValues[item.type]}
            </div>
            <div className={styles.actionsBox}>
              {item.connected ? (
                <img src={ActivatedIcon} alt="activated" />
              ) : null}
              {(item.connected && profileLoginProvider !== item.type) ||
              !item.connected ? (
                <button
                  onClick={
                    item.connected
                      ? () => onClickDisconnect(item.type)
                      : undefined
                  }
                  className={cn({
                    [styles.addBtn]: true,
                    [styles.deleteBtn]: item.connected,
                  })}
                >
                  <PlusIcon />
                </button>
              ) : null}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
})

export default UserInfoConnectSocial
